import { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMsal } from "@azure/msal-react";

import { config } from "../../lib/envConfig";
import { get } from "../../axiosUtils/appUtils";

const AccordionBox = (props) => {
  const { accounts } = useMsal();
  const [uniqueData, setUniqueData] = useState([]);

  const [isUniqueDataLoading, setIsUniqueDataLoading] = useState(true);

  const [data, setData] = useState([]);
  const [expanded, setExpanded] = useState({});

  const [selectedCategory, setSelectedCategory] = useState(null);

  const userEmail = accounts.length > 0 ? accounts[0].username : "Guest";

  const code = config?.VITE_FUNC_KEY_CODE;

  const {
    columns = 2,
    selectedCategories,
    handleCheckboxChange: externalCheckboxHandler,
  } = props;

  // State for filter options
  useEffect(() => {
    const getAdminDetails = async () => {
      const url = `/getAdminFilters?code=${code}&library_id=1&ad_group_id=1&user_email=${userEmail}`;

      try {
        const response = await get(url);
        const fetchedData = response?.data?.data;
        setData(fetchedData);

        // Flatten subcategories and attach parent category
        const subCategories = fetchedData.AdminFilters.flatMap((filter) =>
          filter.SubCategory.map((sub) => ({
            ...sub,
            Category: filter.Category,
          }))
        );

        // Now apply filtering based on selectedCategories["Category"]
        const selected = Object.values(selectedCategories).flat();

        let filteredSubCategories = subCategories;

        if (selected.length > 0) {
          filteredSubCategories = subCategories.filter((sub) =>
            selected.includes(sub.Category)
          );
        }

        // Group by Title and merge Values
        const groupedMap = {};
        filteredSubCategories.forEach((sub) => {
          if (!groupedMap[sub.Title]) {
            groupedMap[sub.Title] = { ...sub, Value: [] };
          }
          groupedMap[sub.Title].Value = [
            ...new Set([...groupedMap[sub.Title].Value, ...(sub.Value || [])]),
          ];
        });

        // If no groups after filtering, fall back to default (initial) grouping from all subCategories
        let unique = Object.values(groupedMap);
        if (unique.length === 0) {
          const defaultGrouped = {};
          subCategories.forEach((sub) => {
            if (!defaultGrouped[sub.Title]) {
              defaultGrouped[sub.Title] = { ...sub, Value: [] };
            }
            defaultGrouped[sub.Title].Value = [
              ...new Set([...defaultGrouped[sub.Title].Value, ...(sub.Value || [])]),
            ];
          });
          unique = Object.values(defaultGrouped);
        }

        setUniqueData(unique);
        setIsUniqueDataLoading(false);
      } catch (error) {
        console.error("Error fetching admin filters:", error);
      }
    };

    getAdminDetails();
  }, [code, userEmail, selectedCategories]);

  // Handle checkbox change
  const handleCheckboxChange = (key, item) => {
    if (key === "Category") {
      const isAlreadySelected = selectedCategories[key]?.includes(item);
      setSelectedCategory(isAlreadySelected ? null : item);
    }
    externalCheckboxHandler(key, item);
  };

  // Merge all accordion sections
  const mergedAccordions = [
    {
      title: "Category",
      items: data?.UserCategories?.split(",") || [],
      key: "Category",
    },
    {
      title: "List Status",
      items: data?.AdminRGB?.map(str => str.charAt(0).toUpperCase() + str.slice(1)) || [],
      key: "rgb",
    },
    {
      title: "Status",
      items: data?.AdminStatus?.map((item) => item.status) || [],
      key: "status",
    },
    // {
    //   title: "Vendor",
    //   items: data?.AdminVendors?.map((item) => item.VendorName) || [],
    //   key: "Remove Vendor",
    // },
    ...uniqueData?.map((sub) => ({
      title: sub.Title,
      items: sub.Value,
      key: sub.Title,
    })),
  ];

  const handleAccordionChange = (key) => (event, isExpanded) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: isExpanded,
    }));
  };

  return (
    <>
      {
      isUniqueDataLoading ? (
        <Box sx={{ alignItems: "center", paddingTop: "200px" , paddingLeft: "150px"}}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {mergedAccordions.slice(0, 3).map(({ title, items, key }) => (
            <Accordion
              key={key}
              sx={{
                boxShadow: "none",
                borderBottom: "1px solid #e5e7eb",
                width: "330px",
                margin: "0 !important",
              }}
              expanded={
                expanded[key] ?? ["Category", "rgb", "status"].includes(key)
              }
              onChange={handleAccordionChange(key)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${key}-content`}
                id={`${key}-header`}
                sx={{
                  padding: "0px !important",
                  margin: "0px !important",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    minHeight:
                      expanded[key] ?? ["Category", "rgb", "status"].includes(key)
                        ? "10px"
                        : "30px",
                    height:
                      expanded[key] ?? ["Category", "rgb", "status"].includes(key)
                        ? "10px"
                        : "30px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${columns}, 1fr)`,
                    rowGap: 0,
                  }}
                >
                  {items?.map((item, idx) => (
                    <FormControlLabel
                      key={idx}
                      control={
                        <Checkbox
                          checked={selectedCategories[key]?.includes(item)}
                          onChange={() => handleCheckboxChange(key, item)}
                          size="small"
                        />
                      }
                      label={item}
                      sx={{ margin: 0 }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Show uniqueData accordions or 'Details not found' */}
          {uniqueData && uniqueData.length === 0 ? (
            <Box sx={{ paddingTop: "200px", paddingLeft: "48px" }}>
              This combination doesnt exist. Please click on Reset Filter.
            </Box>
          ) : (
            mergedAccordions.slice(3).map(({ title, items, key }) => (
              <Accordion
                key={key}
                sx={{
                  boxShadow: "none",
                  borderBottom: "1px solid #e5e7eb",
                  width: "330px",
                  margin: "0 !important",
                }}
                expanded={
                  expanded[key] ?? false
                }
                onChange={handleAccordionChange(key)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${key}-content`}
                  id={`${key}-header`}
                  sx={{
                    padding: "0px !important",
                    margin: "0px !important",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      minHeight: expanded[key] ? "10px" : "30px",
                      height: expanded[key] ? "10px" : "30px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${columns}, 1fr)`,
                      rowGap: 0,
                    }}
                  >
                    {items?.map((item, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={
                          <Checkbox
                            checked={selectedCategories[key]?.includes(item)}
                            onChange={() => handleCheckboxChange(key, item)}
                            size="small"
                          />
                        }
                        label={item}
                        sx={{ margin: 0 }}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </>
      )}
    </>
  );
};

export default AccordionBox;
